// Shared helpers for Resource injects on the Exercise Timeline.
// Resources are persisted as backend timeline items with group === 'resource'.

import { cleanRoles } from './ScenarioRoleField';

export const RESOURCE_GROUP = 'resource';

export const AVAILABILITY = {
  once: { label: 'Single-use', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  reuse: { label: 'Reusable', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  time: { label: 'Time-based', color: 'text-amber-600 bg-amber-50 border-amber-200' },
};

export const UNITS = [
  { value: 'min', label: 'minute(s)' },
  { value: 'hour', label: 'hour(s)' },
  { value: 'day', label: 'day(s)' },
];

export const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
export const money = (v) => num(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const unitLabel = (u) => UNITS.find((x) => x.value === u)?.label || u;

export const blankResource = () => ({
  id: '',
  category: '',
  quantity: 1,
  availability: 'once',
  replenishEvery: '',
  replenishUnit: 'min',
  cost: 0,
  slaMinutes: '',
  assigneeType: '',
  assigneeId: '',
  assigneeName: '',
  assigneeParentId: '',
  roles: [],
  note: '',
});

// ---- Resources reference module (global catalog) mapping helpers ----
// Catalog items live in the backend `resources` collection (snake_case);
// the form shape is the one used by ResourceFormModal (camelCase).

export const catalogToResourceForm = (c) => ({
  ...blankResource(),
  category: c?.name || '',
  quantity: c?.quantity ?? 1,
  availability: c?.availability || 'once',
  replenishEvery: c?.replenish_every || '',
  replenishUnit: c?.replenish_unit || 'min',
  cost: c?.unit_cost ?? 0,
});

export const resourceFormToCatalogPayload = (r) => ({
  name: (r.category || '').trim(),
  quantity: num(r.quantity),
  unit_cost: num(r.cost),
  availability: r.availability || 'once',
  replenish_every: r.replenishEvery || '',
  replenish_unit: r.replenishUnit || 'min',
});

// Map a resource form object -> timeline create payload (group 'resource')
export const resourceToTimelinePayload = (r) => {
  const av = AVAILABILITY[r.availability] || AVAILABILITY.once;
  const qty = num(r.quantity);
  const detailParts = [`${qty} unit(s)`, av.label];
  if (r.availability === 'time' && r.replenishEvery) {
    detailParts.push(`every ${r.replenishEvery} ${unitLabel(r.replenishUnit)}`);
  }
  if (r.assigneeName) detailParts.push(`→ ${r.assigneeName}`);
  const hasSla = r.slaMinutes !== '' && r.slaMinutes != null;
  return {
    group: RESOURCE_GROUP,
    title: r.category || 'Resource',
    details: detailParts.join(' · '),
    actor: {
      id: r.assigneeId || 'system',
      name: r.assigneeName || 'System',
      type: r.assigneeType || 'system',
    },
    chips: [av.label],
    attachments: [],
    meta: {
      sla: hasSla ? { withinMinutes: Number(r.slaMinutes) } : null,
      scenarioRoles: cleanRoles(r.roles),
      assignmentNote: r.note || '',
      resource: {
        category: r.category || '',
        quantity: qty,
        availability: r.availability || 'once',
        replenishEvery: r.replenishEvery || '',
        replenishUnit: r.replenishUnit || 'min',
        cost: num(r.cost),
        assigneeType: r.assigneeType || '',
        assigneeId: r.assigneeId || '',
        assigneeName: r.assigneeName || '',
        assigneeParentId: r.assigneeParentId || '',
      },
    },
  };
};

// Build a full timeline item for a PUT (replace) edit operation.
export const buildResourceFullItem = (existingItem, r) => {
  const payload = resourceToTimelinePayload(r);
  const existingResource = existingItem.meta?.resource || {};
  return {
    ...existingItem,
    group: RESOURCE_GROUP,
    title: payload.title,
    details: payload.details,
    actor: payload.actor,
    chips: payload.chips,
    meta: { ...(existingItem.meta || {}), sla: payload.meta.sla, scenarioRoles: payload.meta.scenarioRoles, assignmentNote: payload.meta.assignmentNote, resource: { ...existingResource, ...payload.meta.resource } },
  };
};

// Map a timeline item (group 'resource') -> resource form object
export const timelineItemToResource = (item) => {
  const m = (item.meta && item.meta.resource) || {};
  return {
    id: item.id,
    category: m.category ?? item.title ?? '',
    quantity: m.quantity ?? 1,
    availability: m.availability ?? 'once',
    replenishEvery: m.replenishEvery ?? '',
    replenishUnit: m.replenishUnit ?? 'min',
    cost: m.cost ?? 0,
    consumed: m.consumed ?? 0,
    usageSummary: m.usageSummary || {},
    usageEvents: Array.isArray(m.usageEvents) ? m.usageEvents : [],
    slaMinutes: item.meta?.sla?.withinMinutes ?? '',
    assigneeType: m.assigneeType ?? '',
    assigneeId: m.assigneeId ?? '',
    assigneeName: m.assigneeName ?? '',
    assigneeParentId: m.assigneeParentId ?? '',
    roles: Array.isArray(item.meta?.scenarioRoles) ? item.meta.scenarioRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })) : [],
    note: item.meta?.assignmentNote ?? '',
  };
};
